import {
  EFFORT_STATUS_TYPE_ENUM,
  PROJECT_STEP_PRIORITY_TYPE_ENUM,
  PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM,
} from 'enums';
import {
  Box,
  Button,
  MenuItem,
  useTheme,
  TextField,
  FormLabel,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { EffortItem, Project, User } from 'library/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderAutoCompleteLocaleText, renderDatePickerLocaleText } from 'utils';
import { CreateProjectStepMode, ProjectStepGridRow } from 'pages/project-step-managing/types';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

type CreateProjectStepFormParams = {
  bookId: Book | null;
  projectId: Project | null;
  title: string;
  description: string;
  relatedContactId: User | null;
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM;
  dueDate: Dayjs | null;
  notes: string;
  estimatedTaskDay: string;
  alternativeRelatedContactId: User | null;
  responsibleContactType: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM;
  additionalEffortDay: string;
  comments: string[];
  responsibleCustomerFullName: string;
};

export type CreateProjectStepPostFormParams = Omit<
  CreateProjectStepFormParams,
  | 'bookId'
  | 'projectId'
  | 'relatedContactId'
  | 'estimatedTaskDay'
  | 'alternativeRelatedContactId'
  | 'additionalEffortDay'
  | 'dueDate'
> & {
  bookId: number;
  projectId: number;
  relatedContactId: number | null;
  estimatedTaskDay: number;
  alternativeRelatedContactId: number | null;
  additionalEffortDay: number;
  dueDate: string;
  parentTaskId: number | null;
};

const defaultValues: CreateProjectStepFormParams = {
  bookId: null,
  projectId: null,
  title: '',
  description: '',
  relatedContactId: null,
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
  dueDate: null,
  notes: '',
  estimatedTaskDay: '',
  alternativeRelatedContactId: null,
  responsibleContactType: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
  additionalEffortDay: '',
  comments: [],
  responsibleCustomerFullName: '',
};

const EXCLUDED_EFFORT_STATUSES = new Set([
  EFFORT_STATUS_TYPE_ENUM.IN_SALES,
  EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL,
  EFFORT_STATUS_TYPE_ENUM.REJECTED,
]);

type CreateProjectStepDialogProps = {
  open: boolean;
  handleClose: () => void;
  books: Book[];
  products: Project[];
  users: User[];
  priorityOptions: {
    value: PROJECT_STEP_PRIORITY_TYPE_ENUM;
    label: string | undefined;
  }[];
  responsibleContactOptions: {
    value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM;
    label: string | undefined;
  }[];
  refresh: () => Promise<void>;
  mode?: CreateProjectStepMode;
  parentTask?: ProjectStepGridRow | null;
};

const CreateProjectStepDialog = ({
  open,
  handleClose: closeDialog,
  books,
  products,
  users,
  priorityOptions,
  responsibleContactOptions,
  refresh,
  mode = 'main-task',
  parentTask = null,
}: CreateProjectStepDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const isEffortFillingRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [efforts, setEfforts] = useState<EffortItem[]>([]);
  const [selectedEffort, setSelectedEffort] = useState<EffortItem | null>(null);

  // form validations & form state
  const schema = yup
    .object({
      bookId: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      projectId: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      title: yup
        .string()
        .trim()
        .max(
          50,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 50 }
          )
        )
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      description: yup
        .string()
        .trim()
        .max(
          250,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 250 }
          )
        )
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      relatedContactId: yup
        .mixed()
        .nullable()
        .when('responsibleContactType', {
          is: (value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM) =>
            value !== PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
          then: (schema) =>
            schema.required(
              intl.formatMessage({
                id: 'validations.required',
                defaultMessage: 'Bu alan boş bırakılamaz',
              })
            ),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      dueDate: yup
        .mixed<Dayjs>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .test(
          'is-dayjs',
          intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
          (v) => dayjs.isDayjs(v) && v.isValid()
        )
        .test(
          'start-not-in-past',
          intl.formatMessage({
            id: 'validations.dateCannotBePast',
            defaultMessage: 'Tarih geçmiş olamaz',
          }),
          (start) => {
            if (!dayjs.isDayjs(start) || !start.isValid()) return true;

            return !start.isBefore(dayjs().startOf('day'), 'day');
          }
        ),
      notes: yup
        .string()
        .trim()
        .max(
          250,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 250 }
          )
        )
        .notRequired(),
      estimatedTaskDay: yup
        .string()
        .trim()
        .notRequired()
        .matches(/^(?:0\.[0-9]*[1-9][0-9]*|[1-9][0-9]*(?:\.[0-9]+)?)$/, {
          message: intl.formatMessage({
            id: 'validations.onlyDigits',
            defaultMessage: 'Sadece sayı girilmelidir',
          }),
          excludeEmptyString: true,
        }),
      alternativeRelatedContactId: yup.mixed().nullable().notRequired(),
      responsibleContactType: yup
        .mixed<PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      responsibleCustomerFullName: yup
        .string()
        .trim()
        .when('responsibleContactType', {
          is: (value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM) =>
            value === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER ||
            value === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON,
          then: (schema) =>
            schema.required(
              intl.formatMessage({
                id: 'validations.required',
                defaultMessage: 'Bu alan boş bırakılamaz',
              })
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      additionalEffortDay: yup
        .string()
        .trim()
        .notRequired()
        .matches(/^(?:0\.[0-9]*[1-9][0-9]*|[1-9][0-9]*(?:\.[0-9]+)?)$/, {
          message: intl.formatMessage({
            id: 'validations.onlyDigits',
            defaultMessage: 'Sadece sayı girilmelidir',
          }),
          excludeEmptyString: true,
        }),
    })
    .required();

  const { reset, watch, control, setValue, handleSubmit, clearErrors } = useForm<CreateProjectStepFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // dynamic variables
  const watchBook = watch('bookId');
  const watchResponsibleContactType = watch('responsibleContactType');

  // async helper functions
  const onSubmit = async (data: CreateProjectStepFormParams) => {
    if (
      !data.bookId ||
      !data.projectId ||
      !data.dueDate ||
      (data.responsibleContactType !== PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER && !data.relatedContactId)
    )
      return;

    const postData: CreateProjectStepPostFormParams = {
      ...data,
      responsibleCustomerFullName:
        data.responsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON
          ? ''
          : data.responsibleCustomerFullName,
      bookId: data.bookId.id,
      projectId: data.projectId.id,
      relatedContactId: data.relatedContactId?.id ?? null,
      dueDate: data.dueDate.toISOString(),
      estimatedTaskDay: Number(data.estimatedTaskDay),
      alternativeRelatedContactId: data.alternativeRelatedContactId?.id ?? null,
      additionalEffortDay: Number(data.additionalEffortDay),
      parentTaskId: mode === 'sub-task' ? (parentTask?.id ?? null) : null,
    };

    try {
      setLoading(true);

      const response = await apiService.createProjectStep(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          mode === 'sub-task' ? (
            <FormattedMessage
              id="projectStepManaging.createSubTaskCompleted"
              defaultMessage="Alt adım oluşturma işlemi başarıyla tamamlandı."
            />
          ) : (
            <FormattedMessage
              id="projectStepManaging.createProjectStepCompleted"
              defaultMessage="Proje Adımı oluşturma işlemi başarıyla tamamlandı."
            />
          ),
          { variant: 'success' }
        );

        handleClose();

        await refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset(defaultValues);
    setSelectedEffort(null);
  };

  // useEffect hooks
  useEffect(() => {
    if (mode === 'sub-task') return;

    if (isEffortFillingRef.current) {
      isEffortFillingRef.current = false;
      return;
    }

    setValue('projectId', null);
  }, [watchBook, setValue, mode]);

  useEffect(() => {
    if (!open) return;

    reset({
      ...defaultValues,
      bookId: mode === 'sub-task' ? (books.find((book) => book.id === parentTask?.bookId) ?? null) : null,
      projectId:
        mode === 'sub-task' ? (products.find((product) => product.id === parentTask?.projectId) ?? null) : null,
    });
  }, [open, mode, parentTask, books, products, reset]);

  useEffect(() => {
    if (watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON) {
      setValue('responsibleCustomerFullName', '');
    }
    if (watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER) {
      clearErrors('relatedContactId');
    }
  }, [watchResponsibleContactType, setValue, clearErrors]);

  useEffect(() => {
    if (!open) return;

    const fetchEfforts = async () => {
      try {
        const response = await apiService.getEfforts({ statusType: null, appointedConsultantId: null });

        if (response.isSuccess) {
          const filtered = (response.data ?? []).filter(
            (e) => !EXCLUDED_EFFORT_STATUSES.has(e.status as EFFORT_STATUS_TYPE_ENUM)
          );
          setEfforts(filtered);
        }
      } catch {
        setEfforts([]);
      }
    };

    fetchEfforts();
  }, [open]);

  const handleEffortSelect = useCallback(
    async (effort: EffortItem | null) => {
      setSelectedEffort(effort);
      if (!effort) return;

      const book = books.find((b) => b.id === effort.bookId) ?? null;
      const project = products.find((p) => p.id === effort.projectId) ?? null;
      const user = users.find((u) => u.id === effort.consultantId) ?? null;

      isEffortFillingRef.current = true;
      setValue('bookId', book);
      setValue('projectId', project);
      setValue('estimatedTaskDay', effort.effortDay ? String(effort.effortDay) : '');
      setValue('relatedContactId', user);
      setValue('alternativeRelatedContactId', user);
      setValue('responsibleCustomerFullName', effort.customerFullName ?? '');
      setValue('description', effort.explanation ?? '');
      setValue('notes', effort.consultantNote ?? '');

      // Calculate dueDate from the latest existing project step's dueDate + effortDay
      try {
        const response = await apiService.getProjectSteps({
          statusType: null,
          projectId: effort.projectId,
          bookId: effort.bookId,
          contactId: null,
        });

        if (response.isSuccess && response.data?.length) {
          const latestStep = response.data.reduce((latest, current) => {
            if (!latest.dueDate) return current;
            if (!current.dueDate) return latest;

            return dayjs(current.dueDate).isAfter(dayjs(latest.dueDate)) ? current : latest;
          });

          if (latestStep.dueDate) {
            const newDueDate = dayjs(latestStep.dueDate).add(effort.effortDay ?? 0, 'day');
            setValue('dueDate', newDueDate);
          }
        }
      } catch {
        return;
      }
    },
    [books, products, users, setValue]
  );

  const filteredEfforts = useMemo(() => {
    if (mode === 'sub-task' && parentTask) {
      return efforts.filter((e) => e.bookId === parentTask.bookId && e.projectId === parentTask.projectId);
    }

    return efforts;
  }, [efforts, mode, parentTask]);

  return (
    <ResponsiveDialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}>
        <Box sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
          {mode === 'sub-task' ? (
            <FormattedMessage id="projectStepManaging.createSubTask" defaultMessage="Alt Adım Oluştur" />
          ) : (
            <FormattedMessage id="projectStepManaging.createProjectStep" defaultMessage="Proje Adımı Oluştur" />
          )}
        </Box>
        <Autocomplete
          size="small"
          options={filteredEfforts}
          value={selectedEffort}
          onChange={(_, newValue) => handleEffortSelect(newValue)}
          getOptionLabel={(option: EffortItem) => option.trackNo}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          {...renderAutoCompleteLocaleText(intl)}
          slotProps={{
            paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
          }}
          sx={{ width: { xs: '100%', sm: 320 }, minWidth: 0, flexShrink: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={intl.formatMessage({ id: 'effortTracing.uniqueKey', defaultMessage: 'Efor Takip No' })}
              sx={{
                '& .MuiButtonBase-root': {
                  border: 'none',
                  backgroundColor: 'inherit',
                  width: '2.25rem',
                  height: '2.25rem',
                  padding: '0.25rem',
                  [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                  },
                },
              }}
            />
          )}
        />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            {mode === 'sub-task' ? (
              <FormattedMessage
                id="projectStepManaging.createSubTaskText"
                defaultMessage="İlgili alanları girip devam ederek seçili ana adımın altına yeni bir alt adım oluşturabilirsiniz."
              />
            ) : (
              <FormattedMessage
                id="projectStepManaging.createProjectStepText"
                defaultMessage="İlgili alanları girip devam ederek yeni bir proje adımı oluşturabilirsiniz."
              />
            )}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="bookId">
                <FormattedMessage id="effortTracing.bookId" defaultMessage="Proje Seçimi" />
              </FormLabel>
              <Controller
                name="bookId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={mode === 'sub-task'}
                    size="small"
                    options={books}
                    value={value}
                    {...renderAutoCompleteLocaleText(intl)}
                    onChange={(_, newValue: Book | null) => onChange(newValue)}
                    getOptionLabel={(option: Book) => option.companyName ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="projectId">
                <FormattedMessage id="effortTracing.projectId" defaultMessage="İlgili Ürün" />
              </FormLabel>
              <Controller
                name="projectId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={mode === 'sub-task' || !!!watchBook}
                    size="small"
                    options={(() => {
                      const filteredProducts = products.filter((product) =>
                        watchBook?.projects.some((watchBookProduct) => watchBookProduct.id === product.id)
                      );
                      const sortedData = filteredProducts.sort((a, b) => a.title.localeCompare(b.title));
                      return sortedData;
                    })()}
                    value={value}
                    {...renderAutoCompleteLocaleText(intl)}
                    onChange={(_, newValue: Project | null) => onChange(newValue)}
                    getOptionLabel={(option: Project) => option.title}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="priority">
                <FormattedMessage id="projectStepManaging.priority" defaultMessage="Öncelik" />
              </FormLabel>
              <Controller
                name="priority"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}>
                    {priorityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="estimatedTaskDay">
                <FormattedMessage id="productProjectPlan.estimatedTaskDay" defaultMessage="Tahmini Adım Süresi (gün)" />
              </FormLabel>
              <Controller
                name="estimatedTaskDay"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="additionalEffortDay">
                <FormattedMessage id="projectStepManaging.additionalEffortDay" defaultMessage="Ek Efor Süresi (gün)" />
              </FormLabel>
              <Controller
                name="additionalEffortDay"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="dueDate">
                <FormattedMessage id="projectStepManaging.terminateDate" defaultMessage="Termin" />
              </FormLabel>
              <Controller
                name="dueDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    disablePast
                    localeText={renderDatePickerLocaleText(intl)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON ? 4 : 3,
              }}>
              <FormLabel htmlFor="relatedContactId">
                <FormattedMessage id="projectStepManaging.relatedContact" defaultMessage="İlgili Kişisi" />
              </FormLabel>
              <Controller
                name="relatedContactId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={users}
                    value={value}
                    {...renderAutoCompleteLocaleText(intl)}
                    onChange={(_, newValue: User | null) => onChange(newValue)}
                    getOptionLabel={(option: User) => option.nameSurname ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON ? 4 : 3,
              }}>
              <FormLabel htmlFor="alternativeRelatedContactId">
                <FormattedMessage
                  id="projectStepManaging.alternativeRelatedContact"
                  defaultMessage="Yedek İlgili Kişisi"
                />
              </FormLabel>
              <Controller
                name="alternativeRelatedContactId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={users}
                    value={value}
                    {...renderAutoCompleteLocaleText(intl)}
                    onChange={(_, newValue: User | null) => onChange(newValue)}
                    getOptionLabel={(option: User) => option.nameSurname ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON ? 4 : 3,
              }}>
              <FormLabel htmlFor="responsibleContactType">
                <FormattedMessage id="productProjectPlan.responsibleContact" defaultMessage="Sorumlu" />
              </FormLabel>
              <Controller
                name="responsibleContactType"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}>
                    {responsibleContactOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            {watchResponsibleContactType !== PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON && (
              <Grid size={{ xs: 12, md: 3 }}>
                <FormLabel htmlFor="responsibleCustomerFullName">
                  <FormattedMessage
                    id="projectStepManaging.responsibleCustomerFullName"
                    defaultMessage="Müşteri Sorumlu Ad Soyad"
                  />
                </FormLabel>
                <Controller
                  name="responsibleCustomerFullName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="title">
                <FormattedMessage id="projectStepManaging.projectStep" defaultMessage="Proje Adımı" />
              </FormLabel>
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="description">
                <FormattedMessage id="projectStepManaging.stepExplanation" defaultMessage="Adım Açıklaması" />
              </FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="notes">
                <FormattedMessage id="projectStepManaging.notes" defaultMessage="Notlar" />
              </FormLabel>
              <Controller
                name="notes"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default CreateProjectStepDialog;
